import { HdBodygraphPlanetaryActivation, HdBodygraphSide } from '../hd-bodygraph.models';
import { getHdPlanetDisplayConfigByPlanetId, HdPlanetKey } from '../planet-grid/hd-planet-display-config';
import { HdGateInfoRequestedContext } from './hd-gate-info-requested-context.model';

/**
 * Beschrijver voor één activatie-regel in het gate-info panel.
 *
 * Bevat de bouwstenen om in de view een vertaalde headline op te bouwen
 * (`side` + planet-naam) of — wanneer er geen planeet-activatie is — een
 * vertaalde fallback-regel te tonen (`fallbackKind`).
 */
export interface HdGateActivationDescriptor {
  /** Stabiele track-key (uniek binnen één panel-render). */
  readonly key: string;
  /** Id van de onderliggende activatie; `null` voor fallback-regels. */
  readonly activationId: string | null;
  /** Side van de activatie wanneer beschikbaar, anders `null` (fallback). */
  readonly side: HdBodygraphSide | null;
  /** Planeet-key wanneer de activatie aan een planeet gekoppeld is, anders `null`. */
  readonly planetKey: HdPlanetKey | null;
  /**
   * Onvertaalde planeet-naam als ultieme fallback wanneer de planeet niet in
   * de display-config staat (bv. exotische planet-id). De view gebruikt
   * dit alleen als laatste redmiddel; bij voorkeur wordt {@link planetKey}
   * via i18n vertaald.
   */
  readonly planetFallbackLabel: string | null;
  /** Poort waar de activatie staat (`null` bij een fallback-descriptor). */
  readonly gate: number | null;
  /** Lijn waar de activatie staat (`null` bij een fallback-descriptor). */
  readonly line: number | null;
  /**
   * Indicator voor een fallback-regel zonder planeet-activatie:
   *  - `'none'`  → er is geen planeet-activatie voor deze poort;
   *  - `'dummy'` → enkel actief via de editor (manual fill);
   *  - `null`    → reguliere activatie-regel.
   */
  readonly fallbackKind: 'none' | 'dummy' | null;
}

/**
 * Produceert de descriptors die het floating gate-info panel toont voor de
 * gegeven context (zie spec §1.7). Eén descriptor per activatie zodat de
 * view per regel inline planeet-invloed-info kan tonen.
 *
 * Bevat ook fallback-descriptors wanneer er geen planeet-activatie is — de
 * view rendert die via vertaalde tekst (`fallbackKind`).
 */
export function formatHdGateActivationDescriptors(
  context: HdGateInfoRequestedContext,
): readonly HdGateActivationDescriptor[] {
  if (context.source === 'planet-grid-block') {
    return context.activations.map((activation, index) => toActivationDescriptor(activation, index));
  }

  const planetActivations = context.activations.filter(activation => activation.sourceType === 'planet');
  if (planetActivations.length > 0) {
    return planetActivations.map((activation, index) => toActivationDescriptor(activation, index));
  }

  if (context.isDummyActiveViaEditor) {
    return [{ key: 'dummy', activationId: null, side: null, planetKey: null, planetFallbackLabel: null, gate: null, line: null, fallbackKind: 'dummy' }];
  }

  return [{ key: 'none', activationId: null, side: null, planetKey: null, planetFallbackLabel: null, gate: null, line: null, fallbackKind: 'none' }];
}

function toActivationDescriptor(
  activation: HdBodygraphPlanetaryActivation,
  index: number,
): HdGateActivationDescriptor {
  const config = getHdPlanetDisplayConfigByPlanetId(activation.planetId);
  return {
    key: `${activation.id ?? `${activation.side}:${activation.planetId}`}#${index}`,
    activationId: activation.id,
    side: activation.side,
    planetKey: config?.key ?? null,
    planetFallbackLabel: config ? null : activation.planet,
    gate: activation.gate,
    line: activation.line,
    fallbackKind: null,
  };
}
